/**
 * Hook para gestionar el Service Worker de NFCores
 */

import { useEffect, useState } from 'react';

interface ServiceWorkerState {
  isSupported: boolean;
  isRegistered: boolean;
  isOnline: boolean;
  updateAvailable: boolean;
  error: string | null;
}

export const useServiceWorker = () => {
  const [state, setState] = useState<ServiceWorkerState>({
    isSupported: 'serviceWorker' in navigator,
    isRegistered: false,
    isOnline: navigator.onLine,
    updateAvailable: false,
    error: null
  });

  useEffect(() => {
    // Solo registrar en producción
    if (process.env.NODE_ENV !== 'production') {
      console.log('🔧 Service Worker: Deshabilitado en desarrollo');
      return;
    }

    if (!state.isSupported) {
      console.log('❌ Service Worker: No soportado en este navegador');
      return;
    }

    registerServiceWorker();
    setupOnlineOfflineListeners();
    setupServiceWorkerMessageListener();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const registerServiceWorker = async () => {
    try {
      console.log('🔧 Service Worker: Registrando...');
      
      const registration = await navigator.serviceWorker.register('/sw.js', {
        scope: '/'
      });

      console.log('✅ Service Worker: Registrado exitosamente', registration);
      
      setState(prev => ({
        ...prev,
        isRegistered: true,
        error: null
      }));

      // Verificar actualizaciones
      registration.addEventListener('updatefound', () => {
        const newWorker = registration.installing;
        
        if (newWorker) {
          newWorker.addEventListener('statechange', () => {
            if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
              console.log('🆕 Service Worker: Nueva versión disponible');
              setState(prev => ({
                ...prev,
                updateAvailable: true
              }));
            }
          });
        }
      });

      // Verificar si hay un service worker esperando
      if (registration.waiting) {
        setState(prev => ({
          ...prev,
          updateAvailable: true
        }));
      }

    } catch (error) {
      console.error('❌ Service Worker: Error en registro:', error);
      setState(prev => ({
        ...prev,
        error: error instanceof Error ? error.message : 'Error desconocido'
      }));
    }
  };

  const setupOnlineOfflineListeners = () => {
    const updateOnlineStatus = () => {
      setState(prev => ({
        ...prev,
        isOnline: navigator.onLine
      }));
      
      if (navigator.onLine) {
        console.log('🟢 Conexión restaurada');
      } else {
        console.log('🔴 Conexión perdida');
      }
    };

    window.addEventListener('online', updateOnlineStatus);
    window.addEventListener('offline', updateOnlineStatus);

    return () => {
      window.removeEventListener('online', updateOnlineStatus);
      window.removeEventListener('offline', updateOnlineStatus);
    };
  };

  const setupServiceWorkerMessageListener = () => {
    navigator.serviceWorker?.addEventListener('message', (event) => {
      if (event.data.type === 'SW_UPDATED') {
        setState(prev => ({
          ...prev,
          updateAvailable: true
        }));
        
        // Mostrar notificación de actualización
        if (window.confirm(event.data.message)) {
          window.location.reload();
        }
      }
    });
  };

  // Funciones de control
  const updateServiceWorker = async () => {
    try {
      const registration = await navigator.serviceWorker.getRegistration();
      
      if (registration?.waiting) {
        registration.waiting.postMessage({ type: 'SKIP_WAITING' });
        window.location.reload();
      }
    } catch (error) {
      console.error('Error actualizando service worker:', error);
    }
  };

  const cacheEmergencyData = async (webidData: { id: string; [key: string]: unknown }) => {
    try {
      if (navigator.serviceWorker.controller) {
        navigator.serviceWorker.controller.postMessage({
          type: 'CACHE_EMERGENCY_DATA',
          webidData
        });
        console.log('✅ Datos de emergencia enviados para cache');
      }
    } catch (error) {
      console.error('Error cacheando datos de emergencia:', error);
    }
  };

  const clearCache = async () => {
    try {
      const cacheNames = await caches.keys();
      await Promise.all(
        cacheNames.map(cacheName => caches.delete(cacheName))
      );
      console.log('✅ Cache limpiado');
      window.location.reload();
    } catch (error) {
      console.error('Error limpiando cache:', error);
    }
  };

  return {
    ...state,
    updateServiceWorker,
    cacheEmergencyData,
    clearCache
  };
};

/**
 * Hook para mostrar notificaciones de estado de conexión
 */
export const useConnectionStatus = () => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [showNotification, setShowNotification] = useState(false);

  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      setShowNotification(true);
      setTimeout(() => setShowNotification(false), 3000);
    };

    const handleOffline = () => {
      setIsOnline(false);
      setShowNotification(true);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  return {
    isOnline,
    showNotification,
    hideNotification: () => setShowNotification(false)
  };
};
